/** External Depepdencneis */
import React, { useCallback, useEffect, useMemo, useContext, useRef } from 'react';

/** Internal Depepdencneis */
import { SELECT_TOOL } from '../../actions';
import { TABS_TOOLS, TOOLS_ITEMS } from '../../components/tools/tools.constants';
import { TABS_IDS, TOOLS_IDS } from '../../utils/constants';
import { useStore } from '../../hooks';
import { StyledToolsBar, StyledToolsBarItems } from './ToolsBar.styled';
import ToolsBarItemOptionsWrapper from './ToolsBarItemOptionsWrapper';
import MultiSelectionOptions from './MultiSelectionOptions';
import AdjustTabOptions, { ADJUST_TAB_IDS } from './AdjustTabOptions';
import DrawInspectorTextContentField from '../common/DrawInspectorTextContentField';
import Filters from '../tools/Filters/Filters';
import { EditorContext } from '../../../../../imageEditorComponent'

const ToolsBar = () => {
  const {
    t,
    dispatch,
    tabId,
    toolId,
    annotations,
    selectionsIds = [],
    config: { defaultTabId, defaultToolId, useCloudimage },
  } = useStore();
  const { currentId, currentTab, setCurrentId, setCurrentTab } = useContext(EditorContext);
  const activeTabId = tabId ?? null;
  const fallbackToolId =
    activeTabId &&
    (defaultToolId != null ? defaultToolId : TABS_TOOLS[activeTabId]?.[0]);

  useEffect(() => {
    if (currentTab !== tabId) {
      setCurrentTab(tabId ?? null);
    }
  }, [currentTab, setCurrentTab, tabId]);

  const tabTools = useMemo(
    () => (activeTabId ? TABS_TOOLS[activeTabId] || [] : []),
    [activeTabId],
  );

  const prevActiveTabIdRef = useRef(activeTabId);

  const selectTool = useCallback((newToolId) => {
    setCurrentId('');
    dispatch({
      type: SELECT_TOOL,
      payload: {
        toolId: newToolId,
      },
    });
  }, [dispatch, setCurrentId]);

  const items = useMemo(
    () =>
      tabTools.map((id) => {
        const { Item, hideFn } = TOOLS_ITEMS[id];
        return (
          Item &&
          <Item
            key={id}
            selectTool={selectTool}
            t={t}
            isSelected={toolId === id}
          />
        );
      }),
    [tabTools, toolId, selectTool, t],
  );

  const ToolOptionsComponent = useMemo(() => {
    if (!activeTabId || !toolId) {
      return false;
    }

    if (activeTabId === TABS_IDS.ANNOTATE) {
      const selectionsLength = selectionsIds.length;
      if (selectionsLength === 1) {
        const selectedAnnotation = annotations[selectionsIds[0]];
        return TOOLS_ITEMS[selectedAnnotation.name]?.ItemOptions;
      }
      if (selectionsLength > 1) {
        return MultiSelectionOptions;
      }
    }

    return (
      activeTabId &&
      toolId &&
      TABS_TOOLS[activeTabId].includes(toolId) &&
      (!TOOLS_ITEMS[toolId]?.hideFn ||
        !TOOLS_ITEMS[toolId]?.hideFn({ useCloudimage })) &&
      TOOLS_ITEMS[toolId]?.ItemOptions
    );
  }, [activeTabId, toolId, annotations, selectionsIds, useCloudimage]);

  useEffect(() => {
    const tabChanged = prevActiveTabIdRef.current !== activeTabId;
    prevActiveTabIdRef.current = activeTabId;

    // Only seed a default tool when entering a tab — not Draw (pick a tool explicitly)
    // and not after Escape clears toolId on Draw.
    if (
      tabChanged &&
      activeTabId &&
      activeTabId !== TABS_IDS.ANNOTATE &&
      !toolId &&
      fallbackToolId
    ) {
      dispatch({
        type: SELECT_TOOL,
        payload: { toolId: fallbackToolId },
      });
    }
  }, [activeTabId, dispatch, fallbackToolId, toolId]);

  if (!activeTabId) {
    return (
      <StyledToolsBar
        className="respark_tools-bar-wrapper respark_tools-bar-wrapper--deselected"
        aria-hidden
      />
    );
  }

  const isAdjustTab = ADJUST_TAB_IDS.has(activeTabId);
  const isFiltersTab = activeTabId === TABS_IDS.FILTERS;

  if (isFiltersTab) {
    return (
      <StyledToolsBar className={`respark_tools-bar-wrapper ${tabId} Filters`}>
        <div className="respark_sidebar-options-card respark_filters-options-card">
          <Filters />
        </div>
      </StyledToolsBar>
    );
  }

  if (isAdjustTab) {
    return (
      <StyledToolsBar className={`respark_tools-bar-wrapper Adjust ${tabId || ''}`}>
        <div className="respark_sidebar-options-card respark_adjust-options-card">
          <AdjustTabOptions
            tabTools={tabTools}
            toolId={toolId}
            selectTool={selectTool}
            t={t}
            useCloudimage={useCloudimage}
          />
        </div>
      </StyledToolsBar>
    );
  }

  const isDrawTab = activeTabId === TABS_IDS.ANNOTATE;

  if (isDrawTab) {
    return (
      <StyledToolsBar className={`respark_tools-bar-wrapper Draw Annotate ${tabId || ''}`}>
        <div className="respark_sidebar-options-card respark_draw-panel-stack">
          {items && (
            <StyledToolsBarItems className="respark_tools-bar respark_draw-tools-strip">
              {items}
            </StyledToolsBarItems>
          )}
          {ToolOptionsComponent && t && (
            <div className="respark_draw-inspector">
              <DrawInspectorTextContentField />
              <ToolsBarItemOptionsWrapper>
                <ToolOptionsComponent t={t} />
              </ToolsBarItemOptionsWrapper>
            </div>
          )}
        </div>
      </StyledToolsBar>
    );
  }

  return (
    <StyledToolsBar className={`respark_tools-bar-wrapper ${tabId}`}>
      {items && (
        <StyledToolsBarItems className="respark_tools-bar">
          {items}
        </StyledToolsBarItems>
      )}
      {ToolOptionsComponent && t && (
        <ToolsBarItemOptionsWrapper>
          {ToolOptionsComponent && <ToolOptionsComponent t={t} />}
        </ToolsBarItemOptionsWrapper>
      )}
    </StyledToolsBar>
  );
};

export default ToolsBar;
